import {Routing} from 'nutshell-core/routing';

import {stripUndefinedProperties} from './strip-undefined-properties';

/**
 * Thunk, returns a function to update the query parameters, which removes archived filter
 *
 * @param  {string} reportPath   - Name of the report
 * @param  {object} oldUrlParams - Current page's url parameters
 * @return {function}              Function to change query params of page
 */
export function generateUrlUpdatingParam(currentPath: string, oldUrlParams: any) {
    return function (newParams: any, shouldMerge?: boolean) {
        const updateSearch = generateUpdatedSearch(oldUrlParams);
        const newSearch = updateSearch(newParams, shouldMerge);

        return `${currentPath}?${newSearch}`;
    };
}

export function generateUpdatedSearch(oldUrlParams: any) {
    /**
     * This will then call a function to return an updated query string
     *
     * Takes an object of parameters for a given key, merges it with existing
     * Removes archived filter, if present, for correct behavior when switching from archive view tabs
     * @param  {Object} newParams           - New url parameters (i.e. {report: {gap: 'm'})
     * @param  {boolean} [shouldMerge]      - Should each parameter object be merged to the corresponding existing param object, or replace it entirely?
     * @return {string}                       New query string with updated params
     */
    return function (newParams: any, shouldMerge?: boolean) {
        let params;
        if (shouldMerge) {
            params = Object.keys(newParams).reduce(
                (paramAccum, paramKey) => {
                    paramAccum[paramKey] =
                        typeof newParams[paramKey] === 'string'
                            ? newParams[paramKey]
                            : {
                                  ...paramAccum[paramKey],
                                  ...newParams[paramKey],
                              };

                    return paramAccum;
                },
                {...oldUrlParams}
            );
        } else {
            params = {
                ...oldUrlParams,
                ...newParams,
            };
        }

        // removes archive filter, used for view tab behavior on list pages
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        params.filter && delete params.filter.archived;

        const cleanParams = stripUndefinedProperties(params);
        const newSearch = Routing.param(cleanParams);

        return newSearch;
    };
}

/**
 * This takes a filter, an array of keys to update, and a new value to set the keys to and returns an updated filter object.
 *
 * Takes an object of parameters for a given key, merges it with existing
 * @param  {Object} filter           - Existing filter object
 * @param  {string[]} keysToUpdate   - Array of keys to update in the object
 * @return {Object} newValue         - Updated value of the passed keys
 */
export function updateFilterValues(
    filter: any | null | undefined,
    keysToUpdate: string[],
    newValue: string
) {
    if (!filter) return {};

    const newFilter: Record<string, any> = {};

    keysToUpdate.forEach((key) => {
        if (filter[key]) {
            newFilter[key] = newValue;
        }
    });

    return newFilter;
}
